import { getScreenType } from "@core/lib/ui/getScreenType";
import { stAnalytics } from "@repo/analytics";
import { Motion } from "@repo/solid-motionone";
import { createBreakpoints } from "@solid-primitives/media";
import { RiBusinessMegaphoneFill } from "solid-icons/ri";
import { TbX } from "solid-icons/tb";
import {
  type Component,
  For,
  type JSXElement,
  Show,
  createContext,
  createSignal,
  children as resolveChildren,
  useContext,
} from "solid-js";
import styles from "./FloatingActionButton.module.css";

// Create context for child components to access close function
const FABContext = createContext<() => void>();

export const useFABClose = () => useContext(FABContext);

interface Props {
  children: JSXElement;
}

export const FloatingActionButton: Component<Props> = (props) => {
  const [isExpanded, setIsExpanded] = createSignal(false);

  // Use same breakpoint system as Navbar.tsx for consistency
  const breakpoints = createBreakpoints({ desktop: "994px" }, { fallbackState: { desktop: false, key: "desktop" } });

  const childrenArray = () => {
    const resolved = resolveChildren(() => props.children);
    const resolvedValue = resolved();
    if (Array.isArray(resolvedValue)) {
      return resolvedValue;
    }
    return resolvedValue ? [resolvedValue] : [];
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded());
    stAnalytics.track("click_tracking", {
      cta: "feedback",
      position: "floating_action_button",
      screen: getScreenType(),
      value: isExpanded() ? "close" : "open",
    });
  };

  const handleChildClose = () => {
    setIsExpanded(false);
  };

  // Only render on desktop using same breakpoint logic as Navbar
  return (
    <Show when={breakpoints.desktop}>
      <FABContext.Provider value={handleChildClose}>
        <div class="fixed bottom-6 right-6 z-50 flex flex-col items-end space-y-3">
          {/* Expanded Menu Items */}
          <Show when={isExpanded()}>
            <Motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2, easing: "ease-out" }}
              class="flex flex-col space-y-2"
            >
              <For each={childrenArray()}>
                {(child, index) => (
                  <Motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.15,
                      delay: index() * 0.05,
                      easing: "ease-out",
                    }}
                  >
                    {child}
                  </Motion.div>
                )}
              </For>
            </Motion.div>
          </Show>

          {/* Main FAB Button */}
          <Motion.button
            type="button"
            onClick={toggleExpanded}
            class={styles.fabButton}
            aria-label={isExpanded() ? "Close menu" : "Open action menu"}
            aria-expanded={isExpanded()}
            animate={{
              rotate: isExpanded() ? 45 : 0,
            }}
            transition={{ duration: 0.2, easing: "ease-out" }}
          >
            <Show when={isExpanded()} fallback={<RiBusinessMegaphoneFill class="w-6 h-6" />}>
              <TbX class="w-6 h-6" />
            </Show>
          </Motion.button>
        </div>
      </FABContext.Provider>
    </Show>
  );
};
